/* jshint loopfunc: true */

/**
 * IBTabsetController is a class that manages the initialization and behavior of a tabset component.
 * It handles tasks such as fetching context, processing tabs, adding and deleting tabs, and setting tab heights.
 */
class IBTabsetController {
    /**
     * Constructor for IBTabsetController.
     * @param {Object} $scope - Angular scope object.
     * @param {Object} TaskDataService - Service for handling task data.
     * @param {Object} $signals - Signal service for emitting events.
     * @param {Object} CMSSortableService - Service for managing sortable elements.
     * @param {Object} $uibModal - Service for opening modal dialogs.
     * @param {Object} DeleteObjectHelperService - Service for deleting objects.
     * @param {Object} $interval - Angular interval service.
     * @param {Object} $timeout - Angular timeout service.
     * @param {Object} $config - Configuration service.
     * @param {Object} $q - Angular promise service.
     * @param {Object} SheetLoadedService - Service for handling sheet loading.
     * @param {Object} $ - jQuery object.
     */
    constructor($scope, TaskDataService, $signals, CMSSortableService, $uibModal, DeleteObjectHelperService, $interval, $timeout, $config, $q, SheetLoadedService, $) {
        this.$scope = $scope;
        this.TaskDataService = TaskDataService;
        this.$signals = $signals;
        this.CMSSortableService = CMSSortableService;
        this.$uibModal = $uibModal;
        this.DeleteObjectHelperService = DeleteObjectHelperService;
        this.$interval = $interval;
        this.$timeout = $timeout;
        this.$config = $config;
        this.$q = $q;
        this.SheetLoadedService = SheetLoadedService;
        this.$ = $;

        this.init();
    }

    /**
     * Initializes the tabset controller.
     * This method sets up the initial state and behavior of the tabset.
     */
    init() {
        const _taskContext = this.TaskDataService.fetchContext();
        this.$scope.processedTabs = [];
        this.$scope.isPDFMode = this.$config.isPDFMode();

        const deleteTabModalOptions = {
            templateUrl: "/views/ib/components/tab/modal/deleteTabModal.html",
            windowClass: "tab-delete",
            keyboard: false,
            backdrop: 'static',
            controller: "DeleteTabModalController",
        };

        for (let tabIdx in this.$scope.contentObj.getTabs()) {
            const tab = this.$scope.contentObj.getTabs()[tabIdx];
            if (typeof tab.content === 'object') {
                const new_uuid = angular.createUUID();
                tab.content.uuid = new_uuid;
                _taskContext.write(new_uuid, tab.content);
                tab.content = new_uuid;
            }

            const tabObj = _taskContext.read(tab.content);

            angular.forEach(tabObj.data.objects, (value, key) => {
                if (typeof value === 'object') {
                    const new_uuid = angular.createUUID();
                    tabObj.data.objects.splice(key, 1, new_uuid);
                    value.uuid = new_uuid;
                    tabObj.data.objects[new_uuid] = value;
                    tabObj.flagAsDirty();
                    _taskContext.write(new_uuid, value);
                }
            });

            const processedTab = {
                objectRefs: tabObj.getObjects(),
                preferences: tabObj.getPreferences(),
                uuid: tabObj.getUUID()
            };
            this.$scope.processedTabs.push(processedTab);
        }

        /**
         * Adds a new tab to the tabset.
         * @param {Array} tabs - Array of tabs.
         */
        this.$scope.onAddNewTab = (tabs) => {
            this.$scope.contentObj.flagAsDirty();
            const newObj = angular.copy(this.CMSSortableService.getNewElements().tab.value[0].content.tabs[0]);
            tabs.push(newObj);
            this.$signals.emit("rendering_rows", this.$scope.rows);
        };

        /**
         * Opens the tab preferences modal.
         * @param {Array} tabs - Array of tabs.
         */
        this.$scope.openTabPreferences = (tabs) => {
            const tabPreferencesModalInstance = this.$uibModal.open({
                templateUrl: "/views/ib/components/tab/modal/tabPreferencesModal.html",
                windowClass: "tab-preferences",
                keyboard: false,
                backdrop: 'static',
                controller: "TabPreferencesModalController",
                resolve: {
                    tabs: function () {
                        return tabs;
                    },
                    rows: function () {
                        return this.$scope.rows;
                    }.bind(this),
                    contentObj: function () {
                        return this.$scope.contentObj
                    },
                    deleteTabModalOptions: function () {
                        return deleteTabModalOptions;
                    }
                }
            });
        };

        /**
         * Deletes a tab from the tabset.
         * @param {string} tabUUID - UUID of the tab to be deleted.
         */
        this.$scope.onDeleteTab = (tabUUID) => {
            const _contentObj = _taskContext.read(this.$scope.rows[this.$scope.rowKey].cols[this.$scope.colKey].list[this.$scope.containerKey].content);
            if (!_contentObj.isShowTabs()) {
                return false;
            }
            let cancelForeach = false;
            _contentObj.flagAsDirty();
            angular.forEach(_contentObj.getTabs(), (tab, tabKey) => {
                if (cancelForeach) {
                    return true;
                }
                if (tab.content === tabUUID && !cancelForeach) {
                    const _tabContent = _taskContext.read(tab.content);
                    if (Object.keys(_tabContent.getObjects()).length > 0) {
                        const deleteTabModalInstance = this.$uibModal.open(deleteTabModalOptions);

                        deleteTabModalInstance.result.then(() => {
                        }, () => {
                            if (deleteTabModalInstance.delete) {
                                const containerEntry = _taskContext.read(_contentObj.data.tabs[tabKey].content);
                                this.DeleteObjectHelperService.deleteSubcontainerByEntry(containerEntry);
                                _contentObj.data.tabs.splice(tabKey, 1);
                                this.$signals.emit("rendering_rows", this.$scope.rows);
                            }
                        });
                    } else {
                        const containerEntry = _taskContext.read(_contentObj.data.tabs[tabKey].content);
                        this.DeleteObjectHelperService.deleteSubcontainerByEntry(containerEntry);
                        _contentObj.data.tabs.splice(tabKey, 1);
                        this.$signals.emit("rendering_rows", this.$scope.rows);
                    }
                    return true;
                }
            });
        };

        const _d = this.$q.defer();
        this.SheetLoadedService.wait(_d.promise, 'tabset');

        let timeout2;
        let maxHeight = 0;
        let setMaxHeight = null;

        /**
         * Sets the maximum height for the tab content.
         */
        const setTabHeight = () => {
            if (setMaxHeight === false) {
                return;
            }
            console.log('setTabHeight');
            const tabContents = $('.' + this.$scope.contentObj.data.uuid + ' .tab-content .tab-pane');
            setMaxHeight = false;
            tabContents.each((index, value) => {
                if (maxHeight < $(value).height()) {
                    maxHeight = $(value).height();
                    setMaxHeight = true;
                }
            });

            if (maxHeight !== 0) {
                tabContents.css('min-height', maxHeight);
            }
            this.$timeout.cancel(timeout2);
            timeout2 = this.$timeout(() => {
                const elementHeight = $('.' + this.$scope.contentObj.data.uuid + ' .tab-content .tab-pane.active').height();
                if (maxHeight < elementHeight) {
                    maxHeight = elementHeight;
                    setMaxHeight = true;
                }
                tabContents.css('min-height', maxHeight);
                this.$timeout.cancel(timeout2);
            }, 500);
        };

        /**
         * Handles tab selection.
         */
        this.$scope.tabSelect = () => {
            this.tabSelect();
            setTabHeight();
        };

        this.$scope.isReady = true;
        const initTabHeight = () => {
            console.log('Initializing tab height');
            this.$scope.isReady = false;
            console.log('isReady set to false');
            let counter = 0;
            let timeout;
            let cheight = 0;
            let oldCheight = 0;
            const interval = this.$interval(() => {
                timeout = this.$timeout(() => {
                    $("." + this.$scope.contentObj.data.uuid + " .tab-content .tab-pane").each((index, value) => {
                        if (counter === $("." + this.$scope.contentObj.data.uuid + " .tab-content .tab-pane").length) {
                            this.$interval.cancel(interval);
                            this.$timeout.cancel(timeout);
                            this.$scope.isReady = true;
                            $("." + this.$scope.contentObj.data.uuid + " .tab-content .tab-pane").css("min-height", cheight + 50);
                            $(".show-tab-pane").removeClass("show-tab-pane");

                            _d.resolve('tabset');
                        } else if (cheight < $(value).height()) {
                            cheight = $(value).height();
                        }
                        counter++;
                    });
                    if (oldCheight !== cheight) {
                        oldCheight = cheight;
                        counter = 0;
                        cheight = 0;
                    }
                }, 500);
            }, 500);
        };
        if (this.$scope.processedTabs.length > 0 && !this.$scope.isPDFMode) {
            initTabHeight();
        }
    }

    /**
     * Handles tab selection by pausing video elements and iframes.
     */
    tabSelect() {
        $('video').each(function () {
            this.pause();
        });

        $('iframe').each(function () {
            try {
                if (angular.isDefined(this.contentWindow) && angular.isDefined(this.contentWindow.videoPause)) {
                    this.contentWindow.videoPause();
                }
            } catch (e) {
                console.warn('videoPause() could not be called on ', this);
            }
        });
    }
}

export default IBTabsetController;